import { AxiosError } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";

import { BlockType } from "../../../../Models/Block";
import { EnhancedOpsSlot } from "../../../../Models/OpsSlot";
import { Error } from "../../../../Utils/Errors";
import OpsEndpointPathsUtility from "../../../../Utils/OpsEndpointPathsUtility";
import { useApi } from "../../../../Utils/useApi";

/** The payload type expected by the slot reservation request endpoint. */
export class CreateSlotReservationPayload {
  /** The ISO datetime value of the slot start time in UTC timezone and format. */
  readonly startTimeUtc: string;
  /** The ISO datetime value of the slot end time in UTC timezone and format. */
  readonly endTimeUtc: string;
  /** The updated occupation state that the slot should be considered as. */
  readonly type: BlockType.Reserved;

  constructor(slot: EnhancedOpsSlot, serviceDurationMinutes: number) {
    this.startTimeUtc = slot.startTime.toUTC().toISO();
    this.endTimeUtc = slot.startTime.toUTC().plus({ minutes: serviceDurationMinutes }).toISO();
    this.type = BlockType.Reserved;
  }
}

interface IUseCreateSlotReservationProps {
  /** The id of the contact which initiated the app session. */
  contactId: string;
  /** The id of the customer for whom the slot reservation is being created. */
  customerId: string;
  /** The id of the center for which the slot reservation is being created. */
  centerId: string;
  /** The duration of the service for which the slot reservation is being created in minutes. */
  serviceDurationMinutes: number;
  /** The id of the service for which the slot reservation is being created. */
  serviceId: string;
}

/** Submit a request to reserve a slot for future appointment creation for the specified customer, center, and service. */
const useCreateSlotReservation: TriggeredHook<
  unknown,
  EnhancedOpsSlot,
  AxiosError<unknown>,
  IUseCreateSlotReservationProps
> = props => {
  const propsRef = useRef<IUseCreateSlotReservationProps>(props);
  const urlRef = useRef("");

  useEffect(() => {
    propsRef.current = props;
  }, [props]);

  const [requestData, setRequestData] = useState<Nullable<CreateSlotReservationPayload>>(null);

  const { response, error, isLoading } = useApi<unknown, CreateSlotReservationPayload>({
    method: "post",
    urlRef,
    data: requestData ?? undefined,
    valid: Boolean(requestData),
    error: Error.PostCenterReservation
  });

  const execute = useCallback((slot: EnhancedOpsSlot) => {
    urlRef.current = OpsEndpointPathsUtility.postAppointmentReservationUrl(
      propsRef.current.contactId,
      propsRef.current.customerId,
      propsRef.current.centerId,
      propsRef.current.serviceId,
      slot.id
    );
    setRequestData(new CreateSlotReservationPayload(slot, propsRef.current.serviceDurationMinutes));
  }, []);

  return {
    result: response ?? null,
    error: error || null,
    execute,
    isLoading
  };
};

export default useCreateSlotReservation;
